import React from "react";
import { Col, Menu, Row } from "antd";
import CgIcon from "../../Pharmacy/common/CgIcon";
import { useListTint } from "../../Pharmacy/common/ListScreen";
import styles from "./pharmacySideMenu.module.css";
import "../../Pharmacy/pharmacyMotion.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";


const EXPANDED_ICON = 18;
/* A touch larger than beside a label, since the mark is all there is - but only
   a touch: at 22 it outgrew its 44px row and read as a button, not an icon. */
const COLLAPSED_ICON = 20;

const menuIcon = (node, label, size) => (
  <span
    role="img"
    aria-label={label}
    className="anticon ant-menu-item-icon"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: size,
      width: size,
      height: size,
      lineHeight: 0,
    }}
  >
    {node}
  </span>
);

const MENU_KEYS = [
  "dashboard",
  "analytics",
  "orders",
  "billing",
  "medicines",
  "configurations",
  "stock-out",
  "purchase-order",
  "purchase-entry",
  "purchase-return",
  "returns",
  "users",
];


const SECTION_ALIASES = {
  "stock-transfer": "medicines",
};

const PharmacySideMenu = ({ collapsed }) => {
  const tint = useListTint();
  const router = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const iconSize = collapsed ? COLLAPSED_ICON : EXPANDED_ICON;

  const getItem = (label, key, iconName) => ({
    key,
    icon: menuIcon(<CgIcon name={iconName} size={iconSize} stroke={1.9} />, label, iconSize),
    label: collapsed ? null : label,
    /*
     * Wrapped rather than passed as a string: antd shows its own tooltip from
     * this, and also hands the same value to rc-menu, which sets it as the
     * native `title` attribute - so a collapsed row raised two tooltips, antd's
     * and the browser's. A node keeps antd's and drops the attribute.
     */
    title: <span>{label}</span>,
  });


  /*
   * The groups survive the fold. Collapsed, the heading has no room for its
   * word, so the stylesheet trades it for a short rule across the rail - the
   * sections stay separated rather than running together into one column of
   * twelve marks. Kept as a group either way so the two widths are the same
   * menu, not two menus.
   */
  const group = (label, children) => [{ type: "group", label, children }];

  const items = [
    getItem("Dashboard", "dashboard", "dashboard"),
    getItem("Analytics & Reports", "analytics", "chart"),
    ...group("Sales", [
      getItem("Orders", "orders", "forms"),
      getItem("Billing", "billing", "rupee2"),
      getItem("Returns", "returns", "undo"),
    ]),
    ...group("Purchase", [
      getItem("Purchase Entry", "purchase-entry", "truck"),
      getItem("Purchase Order", "purchase-order", "clipcheck"),
      getItem("Purchase Return", "purchase-return", "parcelback"),
    ]),
    ...group("Inventory", [
      getItem("Medicines", "medicines", "pill"),
      getItem("Stock Out", "stock-out", "package"),
    ]),
    ...group("Setup", [
      getItem("Configurations", "configurations", "sliders"),
      getItem("Users", "users", "users"),
    ]),
  ];

  const selectedKey = (() => {
    const section = (location.pathname || "").match(/\/pharmacy\/([^/?#]+)/)?.[1];
    const resolved = SECTION_ALIASES[section] || section;
    if (resolved && MENU_KEYS.includes(resolved)) return resolved;
    return router?.tab || "dashboard";
  })();

  const onClick = (e) => {
    navigate(`/pharmacy/${e.key}`);
  };

  return (
    <Row gutter={collapsed ? 0 : [24, 24]}>
      <Col span={24}>
        <Menu
          /*
           * Remounted when the rail opens or closes, so the marks arrive the
           * same way the labelled rows do. The entrance is a CSS animation and
           * plays once per mount; collapsing only swaps the labels out of the
           * same rows, so without this it ran on the first paint and never
           * again.
           */
          key={collapsed ? "rail" : "menu"}
          className={styles.menu}
          style={{ border: 0, "--o-tint": tint }}
          onClick={onClick}
          selectedKeys={[selectedKey]}
          defaultSelectedKeys={[selectedKey]}
          inlineCollapsed={Boolean(collapsed)}
          items={items}
        />
      </Col>
    </Row>
  );
};

export default PharmacySideMenu;
